import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment-timezone';
import { ApplyRootStateType } from '../store';
import {
    replaceBorrowerState,
    updateApiId,
    updateBorrowerFirstLoan,
    updateIsDirty as updateBorrowerIsDirty,
    updateBorrowerShouldAskLegalQuestions,
} from '../borrower/actions';
import {
    replacePropertiesState,
    updateIsDirty as updatePropertiesIsDirty,
    addProperty,
    updateCurrentPropertyId,
} from '../properties/actions';
import { AttachmentUpdater } from '../properties/actions';
import { AttachmentType, Property } from '../properties/types';
import { AppraisalInvoice, formatFullName, IdAndToken } from '../borrower/types';
import { FormType, formAction, formConstant, FormState } from '../forms/types';
import { replaceCollectionState } from '../forms/actions';
import * as UserStoreTypes from '../user/types';
import { updateUserApplications, updateUserExperiences, updateUserNoticeCounts, updateUserNotices } from '../user/actions';
import { LoanType } from '@apply/constants';
import { logApplicationInsightsTrace } from '../../../../apps/admin/src/common/SharedFunctions';

// export default (Component) => withAITracking(reactPlugin, Component)
// export const appInsights = ai.appInsights
export const baseUrl = import.meta.env.VITE_API_URL;

export const authenticatedFetch = async (input: RequestInfo, init: RequestInit = {}, useJson = true): Promise<Response> => {
    const myHeaders = new Headers();
    if (useJson) {
        myHeaders.append('Content-Type', 'application/json');
    }

    const auth = UserStoreTypes.retrieve();
    logApplicationInsightsTrace('auth.user', auth?.user);
    logApplicationInsightsTrace('auth.refreshToken', { refreshToken: auth?.refreshToken });
    if (auth && UserStoreTypes.isRegisteredUser(auth)) {
        logApplicationInsightsTrace('isRegisteredUser');
        if (UserStoreTypes.isExpired(auth)) {
            logApplicationInsightsTrace('isExpired');
            // refresh the token
            const refreshHeaders = new Headers();
            refreshHeaders.append('Content-Type', 'application/json');
            const refreshRequestOptions: RequestInit = {
                method: 'POST',
                headers: refreshHeaders,
                body: JSON.stringify({
                    token: auth.token,
                }),
            };
            const refreshResponse = await fetch(`${baseUrl}/account/refreshToken`, refreshRequestOptions);
            if (refreshResponse.ok) {
                logApplicationInsightsTrace('refreshResponse.ok');
                const refreshResult = await refreshResponse.text();
                if (refreshResult.includes('Invalid Token')) {
                    logApplicationInsightsTrace('includes("Invalid Token")');
                    throw new Error('token refresh failed');
                } else {
                    logApplicationInsightsTrace('DOES NOT includes("Invalid Token")');
                    const data = JSON.parse(refreshResult) as {
                        token: string;
                        refreshToken: string;
                    };
                    const decodedToken = jwtDecode<{ exp: number }>(data.token);
                    const updatedAuth: UserStoreTypes.Authentication = {
                        ...auth,
                        token: data.token,
                        refreshToken: data.refreshToken,
                        expiry: decodedToken.exp * 1000,
                    };
                    logApplicationInsightsTrace('Auth UserStoreTypes.store refresh:', updatedAuth);
                    UserStoreTypes.store(updatedAuth);
                    myHeaders.append('Authorization', `Bearer ${updatedAuth.token}`);
                }
            } else {
                logApplicationInsightsTrace('NOT refreshResponse.ok');
                throw new Error('token refresh failed');
            }
        } else {
            logApplicationInsightsTrace('NOT isExpired');
            myHeaders.append('Authorization', `Bearer ${auth.token}`);
        }
    }

    const requestOptions: RequestInit = {
        ...init,
        headers: myHeaders,
        redirect: 'follow',
    };
    return fetch(input, requestOptions);
};

export const getApplicationRecord = async (apiId: string, token?: string) => {
    token = token ? encodeURIComponent(token) : '';
    const getRequest = await authenticatedFetch(`${baseUrl}/apply/GetApplication/${apiId}${token ? '?token=' + token : ''}`);

    if (!getRequest.ok) {
        if (getRequest.status === 404) {
            throw new Error('fetch of data failed - no data found');
        } else if (getRequest.status === 401) {
            throw new Error('401 - User not Authorized.');
        } else {
            throw new Error('fetch of data failed - server error');
        }
    }

    const body = (await getRequest.json()) as {
        result: { information: ApplyRootStateType; complete: boolean };
    };

    if (!body?.result?.information) {
        throw new Error('fetch of data failed - no data found');
    }
    const { complete } = body.result;
    return { ...body.result.information, complete } as ApplyRootStateType & {
        complete: boolean;
    };
};

export const getInitialBorrowerStateFromUserApplications = async (email: string) => {
    const getRequest = await authenticatedFetch(`${baseUrl}/apply/GetBorrowerFromUserApplications?email=${email}`);

    if (!getRequest.ok) {
        if (getRequest.status === 404) {
            throw new Error('fetch of data failed - no data found');
        } else if (getRequest.status === 401) {
            throw new Error('401 - User not Authorized.');
        } else {
            throw new Error('fetch of data failed - server error');
        }
    }

    const body = (await getRequest.json()) as {
        result: { isFirstLoan: boolean; shouldAskLegalQuestions: boolean };
    };

    if (!body?.result) {
        throw new Error('fetch of data failed - no data found');
    }
    return { ...body.result };
};

// export const loadFormStateFromApi = (
//   apiId: string,
//   setCurrentPropertyId?: string,
//   token?: string
// ): ThunkAction<Promise<boolean | undefined>, ApplyRootStateType, unknown, AnyAction> => async (dispatch, getState) => {

export const loadSavedApplyForms =
    (
        apiId: string,
        loanType: LoanType,
        token?: string
    ): ThunkAction<Promise<FormState[] | undefined>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        let savedForms = undefined;

        token = token ?? getState().borrower.token;
        token = token ? encodeURIComponent(token) : '';
        const getRequest = await authenticatedFetch(`${baseUrl}/apply/GetSavedForms/?loanType=${loanType}`);

        if (!getRequest.ok) {
            if (getRequest.status === 404) {
                throw new Error('fetch of data failed - no data found');
            } else if (getRequest.status === 401) {
                throw new Error('401 - User not Authorized.');
            } else {
                throw new Error('fetch of data failed - server error');
            }
        }

        const body = (await getRequest.json()) as { result: FormState[] };

        if (!body?.result) {
            throw new Error('fetch of data failed - no data found');
        }

        savedForms = body.result;

        if (savedForms) {
            dispatch(
                replaceCollectionState({
                    forms: savedForms,
                    isDirty: false,
                    apiId,
                })
            );
        }

        return savedForms;
    };

// note that this method assumes that the caller has converted the amount into
// a whole number (250.00 = 25000)
export const createPaymentIntent = async (
    amount: number,
    paymentType: string,
    memo: string,
    billingDetails: Record<string, string>
) => {
    const payload = {
        amount,
        paymentType,
        memo,
        ...billingDetails,
    };
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const requestOptions: RequestInit = {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    };
    const response = await fetch(`${baseUrl}/Payment/CustomAmount/`, requestOptions);

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('fetch of data failed - no data found');
        } else {
            throw new Error('fetch of data failed - server error');
        }
    }

    const body = await response.json();

    return body.result.clientSecret;
};

export const sendPaymentReceiptEmail = async (receiptDetails: Record<string, unknown>) => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const requestOptions: RequestInit = {
        method: 'POST',
        headers,
        body: JSON.stringify(receiptDetails),
    };
    const response = await fetch(`${baseUrl}/PaymentIntent/EmailBfi/`, requestOptions);

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('fetch of data failed - no data found');
        } else {
            throw new Error('fetch of data failed - server error');
        }
    }
};

export const saveApplicationRecord = async (
    apiId: string,
    state: ApplyRootStateType,
    complete = false,
    token = null
): Promise<IdAndToken> => {
    const recordName = state.borrower.principal?.name
        ? formatFullName(state.borrower.principal.name)
        : formatFullName(state.borrower.propertyManagementCompany.contactName);
    const recordEmail = state.borrower.principal?.email
        ? state.borrower.principal.email
        : state.borrower.propertyManagementCompany.contactEmail;
    if (!recordEmail || !recordName) {
        // the validation should keep this from ever happening, but just in case
        throw new Error('name and email are required fields to save');
    }
    if (apiId) {
        return await updateApplicationRecord(apiId, state, recordName, recordEmail, complete, state.borrower.token ?? token);
    } else {
        return await createApplicationRecord(state, recordName, recordEmail, complete, token);
    }
};

export const saveSharedFormRecord = async (form: FormState): Promise<number> => {
    let formTypeId = 0;
    switch (form.type) {
        case 'Budget':
            formTypeId = 1;
            break;
        case 'Spec Sheet':
            formTypeId = 2;
            break;
        case 'Fix-Flip':
        case 'Fix-Flip-LOC':
            formTypeId = 3;
            break;
        case 'Land Budget':
            formTypeId = 4;
            break;
    }

    const requestOptions: RequestInit = {
        method: 'PUT',
        body: JSON.stringify({
            id: form.id,
            formType: formTypeId,
            form: JSON.stringify(form),
        }),
    };

    const response = await authenticatedFetch(`${baseUrl}/apply/UpdateSharedForm`, requestOptions);
    if (response.ok) {
        const results = (await response.json()) as { result: number };
        return results.result;
    } else if (response.status === 401) {
        throw new Error('401 - User not Authorized.');
    } else {
        throw new Error('save failed');
    }
};

export const createApplicationRecord = async (
    state: ApplyRootStateType,
    recordName: string,
    recordEmail: string,
    complete: boolean,
    token: string
): Promise<IdAndToken> => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify({
            name: recordName,
            email: recordEmail,
            information: JSON.stringify(state),
            complete,
        }),
    };
    console.debug('apply entry record save payload', JSON.stringify(state));
    const response = await authenticatedFetch(
        `${baseUrl}/apply/PostApplication${token ? '?token=' + token : ''}`,
        requestOptions
    );
    if (response.ok) {
        const results = (await response.json()) as { result: IdAndToken };
        return results.result;
    } else if (response.status === 400) {
        const payload = (await response.json()) as { errors: [string] };
        if (payload.errors.length > 0) {
            throw new Error(payload.errors[0]);
        } else {
            throw new Error('save failed');
        }
    } else {
        throw new Error('save failed');
    }
};

const updateApplicationRecord = async (
    apiId: string,
    state: ApplyRootStateType,
    recordName: string,
    recordEmail: string,
    complete: boolean,
    token: string
): Promise<IdAndToken> => {
    const requestOptions: RequestInit = {
        method: 'PUT',
        body: JSON.stringify({
            entryGuid: apiId,
            name: recordName,
            email: recordEmail,
            information: JSON.stringify(state),
            complete,
        }),
    };
    console.debug('apply entry record save payload', JSON.stringify(state));

    token = token ? encodeURIComponent(token) : '';
    const response = await authenticatedFetch(
        `${baseUrl}/apply/UpdateApplication${token ? '?token=' + token : ''}`,
        requestOptions
    );
    if (response.ok) {
        const results = (await response.json()) as { result: IdAndToken };
        return results.result;
    } else if (response.status === 400) {
        const payload = (await response.json()) as { errors: [string] };
        if (payload.errors.length > 0) {
            throw new Error(payload.errors[0]);
        } else {
            throw new Error('save failed');
        }
    } else if (response.status === 401) {
        throw new Error('401 - User not Authorized.');
    } else {
        throw new Error('save failed');
    }
};

type TemplateHtmlApiResult = {
    result?: string;
    statusCode: number;
};

export const getTermSheetHtml = async (
    logoUrl: string,
    rentalExhibitImageUrl: string,
    loanType: LoanType,
    borrowerName: string,
    borrowerEmail: string,
    borrowerEntity: string,
    borrowerPhone: string,
    borrowerTitle: string
) => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify({
            logoUrl,
            rentalExhibitImageUrl,
            loanType,
            borrowerName,
            borrowerEmail,
            borrowerEntity,
            borrowerPhone,
            borrowerTitle,
        }),
    };

    const response = await authenticatedFetch(`${baseUrl}/templates/termsheets/sign`, requestOptions);
    if (response.ok) {
        const signHtmlResponse = (await response.json()) as TemplateHtmlApiResult;
        return signHtmlResponse.result;
    } else {
        throw new Error('termsheet retrieval failed');
    }
};

export const getElectronicRecordsHtml = async () => {
    const requestOptions: RequestInit = {
        method: 'GET',
    };

    const response = await authenticatedFetch(`${baseUrl}/templates/useragreements/0/html`, requestOptions);
    if (response.ok) {
        const signHtmlResponse = (await response.json()) as TemplateHtmlApiResult;
        return signHtmlResponse.result;
    } else {
        throw new Error('agreement retrieval failed');
    }
};

export const signUserAgreement = async (userId: string, userAgreementTypeId: number, fontId: number, ipAddress: string) => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify({
            userId,
            userAgreementTypeId,
            fontId,
            ipAddress,
        }),
    };

    const response = await authenticatedFetch(`${baseUrl}/account/signuseragreement`, requestOptions);
    const { authentication } = await processLoginResponse(response);
    if (authentication) {
        logApplicationInsightsTrace('Auth processLoginResponse:', authentication);
        UserStoreTypes.store(authentication);
    }
};

export const loadFormStateFromApi =
    (
        apiId: string,
        setCurrentPropertyId?: string,
        token?: string
    ): ThunkAction<Promise<boolean | undefined>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        let formIsComplete = undefined;

        if (apiId) {
            token = token ?? getState().borrower?.token;
            token = token ? encodeURIComponent(token) : '';

            const results = await getApplicationRecord(apiId, token);
            const { borrower, properties } = results;
            // note that these replace actions include resetting isDirty to false
            /* istanbul ignore else */
            if (borrower) {
                dispatch(replaceBorrowerState({ ...borrower, apiId }));
            }
            /* istanbul ignore else */
            if (properties) {
                dispatch(replacePropertiesState(results.properties));
                if (setCurrentPropertyId) {
                    dispatch(updateCurrentPropertyId(setCurrentPropertyId));
                }
                if (!properties?.items?.length) {
                    dispatch(addProperty());
                }
                // if (properties.forms) {
                //   dispatch(replaceCollectionState({forms: properties.forms, isDirty: false, apiId}));
                // }
            }

            const { complete } = results;
            formIsComplete = complete;
        }

        return formIsComplete;
    };

export const loadStateFromApi =
    (
        apiId: string,
        setCurrentPropertyId?: string,
        token?: string
    ): ThunkAction<Promise<boolean | undefined>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        let formIsComplete = undefined;

        if (apiId) {
            token = token ?? getState().borrower.token;
            token = token ? encodeURIComponent(token) : '';

            const results = await getApplicationRecord(apiId, token);
            const { borrower, properties } = results;
            // note that these replace actions include resetting isDirty to false
            /* istanbul ignore else */
            if (borrower) {
                dispatch(replaceBorrowerState({ ...borrower, apiId }));
            }
            /* istanbul ignore else */
            if (properties) {
                dispatch(replacePropertiesState(results.properties));
                if (setCurrentPropertyId) {
                    dispatch(updateCurrentPropertyId(setCurrentPropertyId));
                }
                if (!properties?.items?.length) {
                    dispatch(addProperty());
                }
                if (properties.forms) {
                    dispatch(
                        replaceCollectionState({
                            forms: properties.forms,
                            isDirty: false,
                            apiId,
                        })
                    );
                }
            }

            const { complete } = results;
            formIsComplete = complete;
        }

        return formIsComplete;
    };

export const loadBorrowerStateFromUserApplications =
    (email?: string): ThunkAction<Promise<void | undefined>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch) => {
        if (!email) {
            return;
        }

        const results = await getInitialBorrowerStateFromUserApplications(encodeURIComponent(email));

        // note that these replace actions include resetting isDirty to false
        /* istanbul ignore else */
        if (results) {
            dispatch(updateBorrowerFirstLoan(results.isFirstLoan));
            dispatch(updateBorrowerShouldAskLegalQuestions(results.shouldAskLegalQuestions));
        }

        return;
    };

export const saveStateToApi =
    (apiId: string, complete = false, token = null): ThunkAction<Promise<IdAndToken>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const combined = getState();
        if (!apiId) {
            apiId = getState().borrower.apiId;
        }

        // exclude forms collections here, managed separately on the backend
        const results = await saveApplicationRecord(
            apiId,
            {
                borrower: combined.borrower,
                properties: combined.properties,
                forms: undefined,
                user: undefined,
            },
            complete,
            token
        );
        /* istanbul ignore else */
        if (results) {
            dispatch(updateBorrowerIsDirty(false));
            dispatch(updatePropertiesIsDirty(false));
            dispatch(updateApiId(results.id, results?.token));
            return results;
        } else {
            throw new Error('API save failed');
        }
    };

export const saveSharedFormToApi =
    (form: FormState): ThunkAction<Promise<number>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        // exclude forms collections here, managed separately on the backend
        const results = await saveSharedFormRecord(form);
        /* istanbul ignore else */
        if (results) {
            return results;
        } else {
            throw new Error('API save failed');
        }
    };

export const getAppraisalInvoice = async (entryGuid: string): Promise<AppraisalInvoice> => {
    if (!entryGuid) {
        return;
    }

    const requestOptions: RequestInit = {
        method: 'GET',
    };

    const response = await authenticatedFetch(`${baseUrl}/Payment/PropertyAppraisal/${entryGuid}`, requestOptions);
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('fetch of data failed - no data found');
        } else {
            throw new Error('fetch of data failed - server error');
        }
    }

    const { result } = (await response.json()) as {
        result: AppraisalInvoice;
    };

    if (!result?.invoiceLines?.length) {
        throw new Error('fetch of data failed - no data found');
    }

    return result;
};

export const uploadAttachment =
    (
        apiId: string,
        loanType: LoanType,
        propertyGuid: string,
        fileType: AttachmentType,
        file: File,
        updater: AttachmentUpdater,
        token: string
    ): ThunkAction<Promise<string>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        if (file && updater) {
            let updateStoredApiId = false;

            token = getState().borrower.token ?? token;
            token = token ? encodeURIComponent(token) : '';

            if (!apiId) {
                apiId = getState().borrower.apiId;
            }
            // do the upload
            const formData = new FormData();

            if (apiId) {
                formData.append('applicationId', apiId);
            } else {
                updateStoredApiId = true;
            }
            formData.append('file', file, file.name);
            formData.append('fileType', fileType);
            formData.append('propertyGuid', propertyGuid);
            formData.append('loanType', loanType.toString());

            const response = await authenticatedFetch(
                `${baseUrl}/apply/${apiId ? 'Update' : 'Post'}applicationFile${token ? '?token=' + token : ''}`,
                {
                    body: formData,
                    method: apiId ? 'put' : 'post',
                    headers: { 'Content-Type': 'multipart/form-data' },
                },
                false
            );

            /* istanbul ignore if */
            if (!response.ok) {
                throw new Error('file upload failed');
            }

            const resultsJSON = await response.json();
            const results = resultsJSON as {
                result: {
                    id: string;
                    applicationFileId: string;
                    safeFileName: string;
                    token: string;
                };
            };

            // update redux
            dispatch(
                updater(propertyGuid, {
                    handleMode: 'uploadfile',
                    uploadedFilename: results.result.safeFileName,
                    fileType,
                    uploadedFileApiId: results.result.applicationFileId,
                })
            );

            if (updateStoredApiId) {
                dispatch(updateApiId(results.result.id, results.result.token));
            }

            return results.result.safeFileName;
        }

        return '';
    };

type LoginApiResult = {
    result?: {
        err?: string;
        value?: {
            token: string;
            refreshToken: string;
            user?: {
                userId: string;
                emailAddress: string;
                refreshToken: string;
                isAdmin: boolean;
                isBroker: boolean;
                firstName: string;
                middleName: string;
                lastName: string;
                secondaryEmailAddress: string;
                mobilePhone: string;
                workPhone: string;
                requireTermsheet: boolean;
                requireValuationFee: boolean;
                requireUnderwritingFee: boolean;
                isGeneralContractor: boolean;
                isRegistered: boolean;
            };
            applicant?: {
                eSignAgreementDate: Date;
                isFirstLoan: boolean;
                shouldAskLegalQuestions: boolean;
            };
            isLocEnabled: boolean;
            requiresESign: boolean;
            requiresAdminTermsESign: boolean;
            featureList: [UserStoreTypes.FeatureStatus];
        };
    };
    statusCode: number;
};

export const processLoginResponse = async (
    response: Response
): Promise<{ authentication?: UserStoreTypes.Authentication; err?: string }> => {
    if (!response.ok) {
        return {};
    }

    const results = (await response.json()) as LoginApiResult;

    if (results.err) {
        return { err: results.err };
    }
    if (!results.result) {
        return {};
    }
    if (!results.result.value?.token) {
        return {};
    }

    const decoded = jwtDecode<{ exp: number }>(results.result.value.token);
    const user: UserStoreTypes.User = {
        userId: results.result.value.user.userId,
        email: results.result.value.user.emailAddress,
        firstName: results.result.value.user.firstName,
        middleName: results.result.value.user.middleName,
        lastName: results.result.value.user.lastName,
        secondaryEmail: results.result.value.user.secondaryEmailAddress,
        mobilePhone: results.result.value.user.mobilePhone,
        workPhone: results.result.value.user.workPhone,
        requireTermsheet: results.result.value.user.requireTermsheet,
        requireValuationFee: results.result.value.user.requireValuationFee,
        requireUnderwritingFee: results.result.value.user.requireUnderwritingFee,
        isGeneralContractor: results.result?.value?.user?.isGeneralContractor ?? false,
        isRegistered: results.result?.value?.user?.isRegistered ?? false,

        featureList: results.result.value.featureList,
    };
    const applicant: UserStoreTypes.ApplyUser = {
        eSignAgreementDate: results.result.value.applicant?.eSignAgreementDate,
    };
    const res: UserStoreTypes.Authentication = {
        username: results.result.value.user.emailAddress,
        token: results.result.value.token,
        expiry: decoded.exp * 1000,
        refreshToken: results.result.value.refreshToken,
        user,
        applicant,
        isLocEnabled: results.result.value.isLocEnabled,
        requiresESign: results.result.value.requiresESign,
        featureList: results.result.value.featureList,
    };
    return { authentication: res };
};

export const login =
    (
        email: string,
        password: string,
        ipaddress: string
    ): ThunkAction<Promise<UserStoreTypes.Authentication | undefined>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'POST',
            body: JSON.stringify({
                email,
                password,
                ipaddress,
            }),
        };

        const response = await authenticatedFetch(`${baseUrl}/account/principal/login`, requestOptions);
        const { authentication } = await processLoginResponse(response);
        return authentication;
    };

export const register =
    (
        id: string,
        email: string,
        firstName: string,
        middleName: string,
        lastName: string,
        mobilePhone: string,
        secondaryEmail: string,
        eSignFontId: number,
        ipAddress: string
    ): ThunkAction<Promise<UserStoreTypes.RegisterUserResult>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'POST',
            body: JSON.stringify({
                email,
                firstName,
                middleName,
                lastName,
                mobilePhone,
                secondaryEmail,
                eSignFontId,
                ipAddress,
            }),
        };

        const response = await authenticatedFetch(`${baseUrl}/account/register`, requestOptions);
        if (!response.ok) {
            if (response.status === 400) {
                const results = (await response.json()) as { errors: string[] };

                if (results.errors?.length > 0) {
                    throw new Error(results.errors.join('; '));
                }
            }
            throw new Error('Register failed');
        }

        const results = (await response.json()) as UserStoreTypes.RegisterUserResponse;
        return results.result;
    };

export const validateEmail =
    (
        email: string,
        userType: UserStoreTypes.UserType = UserStoreTypes.UserType.Principal
    ): ThunkAction<Promise<UserStoreTypes.ValidateEmailResult>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await authenticatedFetch(
            `${baseUrl}/account/validateEmail?email=${encodeURIComponent(email)}&userType=${userType}`,
            requestOptions
        );
        if (!response.ok) {
            throw new Error('Email validation failed');
        }

        const results = (await response.json()) as UserStoreTypes.ValidateEmailResponse;
        return results.result;
    };

export const requestBrokerProfile =
    (
        email: string,
        firstName: string,
        middleName: string,
        lastName: string,
        mobilePhone: string
    ): ThunkAction<Promise<UserStoreTypes.RegisterUserResult>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'POST',
            body: JSON.stringify({
                email,
                firstName,
                middleName,
                lastName,
                mobilePhone,
            }),
        };

        const response = await authenticatedFetch(`${baseUrl}/account/brokerprofile/request`, requestOptions);
        if (!response.ok) {
            if (response.status === 400) {
                const results = (await response.json()) as { errors: string[] };
                if (results.errors?.length > 0) {
                    throw new Error(results.errors.join('; '));
                }
            }
            throw new Error('An unexpected error has occurred!');
        }
        const results = (await response.json()) as UserStoreTypes.RegisterUserResponse;
        return results.result;
    };

export type ChangePasswordResponse = {
    statusCode: number;
    result: ChangePasswordResult;
    errors: string[];
};

export type ChangePasswordResult = {
    succeeded: boolean;
    passwordTooShort: boolean;
    passwordRequiresSpecialCharacter: boolean;
    passwordRequiresMixedCase: boolean;
    passwordRequiresDigit: boolean;
};

export type UserProfileResult = {
    id: string;
    email: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    phone: string;
};

export const resetPassword =
    (
        id: string,
        resetToken: string,
        password: string
    ): ThunkAction<Promise<ChangePasswordResponse>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'POST',
            body: JSON.stringify({
                userId: id,
                resetToken,
                password,
            }),
        };

        const response = await authenticatedFetch(`${baseUrl}/account/resetPassword`, requestOptions);
        if (!response.ok) {
            if (response.status === 400) {
                const results = (await response.json()) as { errors: string[] };
                if (results.errors?.length > 0) {
                    throw new Error(results.errors.join('; '));
                }
            }
            throw new Error('create new password failed');
        }
        const results = await response.json();
        return results.result as ChangePasswordResponse;
    };

export const getUserProfile =
    (id: string, token: string): ThunkAction<Promise<UserProfileResult>, ApplyRootStateType, unknown, AnyAction> =>
    async () => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await authenticatedFetch(`${baseUrl}/account/bfiapply/profile?id=${id}&token=${token}`, requestOptions);
        if (!response.ok) {
            if (response.status === 400) {
                const results = (await response.json()) as { errors: string[] };
                if (results.errors?.length > 0) {
                    throw new Error(results.errors.join('; '));
                }
            }
            throw new Error('create new password failed');
        }
        const results = await response.json();
        return results.result as UserProfileResult;
    };

export const getPropertyRoute = (currentForm: FormType | undefined, property: Property) => {
    if (!currentForm) {
        if ([formAction.create, formAction.edit].includes(property?.nonRental?.budget?.handleMode)) {
            return formConstant.name.budget;
        }

        if ([formAction.create, formAction.edit].includes(property?.nonRental?.landBudget?.handleMode)) {
            return formConstant.name.landBudget;
        }

        if ([formAction.create, formAction.edit].includes(property?.nonRental?.fixFlipBudget?.handleMode)) {
            return formConstant.name.fixFlipBudget;
        }

        if ([formAction.create, formAction.edit].includes(property?.nonRental?.specSheet?.handleMode)) {
            return formConstant.name.specSheet;
        }

        return formConstant.name.review;
    } else if (currentForm === formConstant.displayName.budget) {
        if ([formAction.create, formAction.edit].includes(property?.nonRental?.specSheet?.handleMode)) {
            return formConstant.name.specSheet;
        }
    }

    if (
        ['Budget', 'Land Budget', 'Fix-Flip', 'Fix-Flip-LOC'].includes(currentForm) &&
        [formAction.create, formAction.edit].includes(property?.nonRental?.specSheet?.handleMode)
    ) {
        return formConstant.name.specSheet;
    }

    return 'review';
};

export const forgotPassword =
    (email: string): ThunkAction<Promise<boolean>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'POST',
            body: JSON.stringify({ email }),
        };

        const response = await authenticatedFetch(`${baseUrl}/account/forgotPassword`, requestOptions);
        if (!response.ok) {
            if (response.status === 400) {
                const res = await response.text();

                // for some errors, service returns text instead of JSON
                try {
                    JSON.parse(res);
                } catch {
                    throw new Error(res);
                }

                const results = JSON.parse(res) as { errors: string[] };
                if (results.errors?.length > 0) {
                    throw new Error(results.errors.join('; '));
                }
            }
            throw new Error('forgot password request failed');
        }
        return true;
    };

export const getUserApplications =
    (email: string): ThunkAction<Promise<void>, ApplyRootStateType, unknown, AnyAction> =>
    async (dispatch, getState) => {
        if (!email) {
            return;
        }

        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await authenticatedFetch(
            `${baseUrl}/apply/GetUserApplications?email=${encodeURIComponent(email)}`,
            requestOptions
        );
        if (response.ok) {
            const resultsJSON = await response.json();
            const results = resultsJSON as {
                result: {
                    id: string;
                    information: ApplyRootStateType;
                    lastModifiedDateTime: string;
                    complete: boolean;
                }[];
            };
            if (results.result?.length) {
                const applications = results.result.map((entry) => {
                    const data = entry.information;

                    let extendedPropertyNames = '';
                    if (data.properties?.items?.length > 0) {
                        if (
                            data.properties.items[0].address === undefined ||
                            data.properties.items[0].address?.street1 === undefined ||
                            data.properties.items[0].address?.street1 === ''
                        ) {
                            extendedPropertyNames += 'No Street Address Entered';
                        } else {
                            if (data.properties.items.length === 1) {
                                extendedPropertyNames += data.properties.items[0].address?.street1;
                                if (
                                    data.properties.items[0].address?.street2 !== undefined &&
                                    data.properties.items[0].address?.street2 !== ''
                                ) {
                                    extendedPropertyNames += ', ' + data.properties.items[0].address?.street2;
                                }
                            } else {
                                let sameAddress = false;
                                data.properties.items.forEach((p) => {
                                    const matchingAddresses = data.properties.items.filter(
                                        (d) => d.address?.street1 === p.address?.street1
                                    );
                                    if (matchingAddresses.length === data.properties.items.length) {
                                        sameAddress = true;
                                        return;
                                    }
                                });

                                if (sameAddress) {
                                    extendedPropertyNames += data.properties.items[0].address?.street1;
                                    let address2Info = '';
                                    data.properties.items.forEach((p) => {
                                        if (p.address?.street2 !== undefined && p.address?.street2 !== '') {
                                            if (!address2Info.startsWith(',')) {
                                                address2Info += ', ' + p.address?.street2;
                                            } else {
                                                address2Info += ' + ' + p.address?.street2;
                                            }
                                        }
                                    });
                                    if (
                                        data.properties.items[0].address?.street2 !== undefined &&
                                        data.properties.items[0].address?.street2 !== ''
                                    ) {
                                        extendedPropertyNames += address2Info;
                                    }
                                } else {
                                    let sameCity = false;
                                    data.properties.items.forEach((p) => {
                                        const matchingCities = data.properties.items.filter(
                                            (d) => d.address?.city === p.address?.city && d.address?.state === p.address?.state
                                        );
                                        if (matchingCities.length === data.properties.items.length) {
                                            sameCity = true;
                                            return;
                                        }
                                    });

                                    if (sameCity) {
                                        extendedPropertyNames += data.properties.items.length;
                                        let sameStructureType = false;
                                        data.properties.items.forEach((p) => {
                                            const matchingCities = data.properties.items.filter(
                                                (d) => d.structureType === p.structureType
                                            );
                                            if (matchingCities.length === data.properties.items.length) {
                                                sameStructureType = true;
                                                return;
                                            }
                                        });

                                        if (sameStructureType) {
                                            extendedPropertyNames +=
                                                ' ' +
                                                data.properties.items[0].structureType +
                                                's in ' +
                                                data.properties.items[0].address?.city +
                                                ', ' +
                                                data.properties.items[0].address?.state;
                                        } else {
                                            extendedPropertyNames +=
                                                ' Properties in ' +
                                                data.properties.items[0].address?.city +
                                                ', ' +
                                                data.properties.items[0].address?.state;
                                        }
                                    } else {
                                        extendedPropertyNames += data.properties.items.length;
                                        let sameStructureType = false;
                                        data.properties.items.forEach((p) => {
                                            const matchingCities = data.properties.items.filter(
                                                (d) => d.structureType === p.structureType
                                            );
                                            if (matchingCities.length === data.properties.items.length) {
                                                sameStructureType = true;
                                                return;
                                            }
                                        });

                                        if (sameStructureType) {
                                            extendedPropertyNames += ' ' + data.properties.items[0].structureType + 's';
                                        } else {
                                            extendedPropertyNames += ' Properties';
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        extendedPropertyNames += 'No Street Address Entered';
                    }

                    return {
                        apiId: entry.id,
                        complete: entry.complete,
                        lastModified: moment.utc(entry.lastModifiedDateTime),
                        propertyCount: data.properties?.items?.length || 0,
                        propertyNames: data.properties?.items?.map((prop) => prop.address?.street1).join('; ') || '',
                        extendedPropertyNames,
                    };
                });
                dispatch(updateUserApplications(applications));
            }
        }
    };

export interface IPaging {
    skip: number;
    take: number;
}

//TODO: Deprecate after Apply V2 UI Rollout
export const getUserNoticeCounts =
    (): ThunkAction<Promise<void>, ApplyRootStateType, unknown, AnyAction> => async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await authenticatedFetch(`${baseUrl}/notices/user/counts`, requestOptions);
        if (response.ok) {
            const resultsJSON = await response.json();
            const results = resultsJSON as {
                result: { total: number; unread: number };
            };
            if (results.result) {
                dispatch(updateUserNoticeCounts(results.result));
            }
        }
    };
//ENDTODO: Deprecate after Apply V2 UI Rollout

export const getUserNotices =
    (): ThunkAction<Promise<void>, ApplyRootStateType, unknown, AnyAction> => async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await authenticatedFetch(`${baseUrl}/notices/user`, requestOptions);
        if (response.ok) {
            const resultsJSON = await response.json();
            const results = resultsJSON as {
                result: UserStoreTypes.UserNoticeLean[];
            };
            if (results.result?.length) {
                const notices = results.result.map((entry) => {
                    return {
                        id: entry.id,
                        noticeTypeId: entry.noticeTypeId,
                        noticeDate: moment.utc(entry.noticeDate),
                        isRead: entry.isRead,
                        eSignDate: entry ? moment.utc(entry.eSignDate) : null,
                        eSignName: entry.eSignName,
                        eSignTypeId: entry.eSignTypeId,
                        canDelete: entry.canDelete,
                        noticeFile: entry.noticeFile,
                        signedFile: entry.signedFile,
                        isManual: entry.isManual,
                        addresses: entry.addresses,
                    };
                });
                dispatch(updateUserNotices(notices));
            }
        }
    };

export const getUserNotice = async (id: number): Promise<UserStoreTypes.UserNotice> => {
    const requestOptions: RequestInit = {
        method: 'GET',
    };

    const response = await authenticatedFetch(`${baseUrl}/notices/user/${id}`, requestOptions);
    if (response.ok) {
        const resultsJSON = await response.json();
        const results = resultsJSON as {
            result: UserStoreTypes.UserNotice;
        };
        return results.result;
    }
};

export const signUserNotice = async (signedNotice: UserStoreTypes.SignUserNotice): Promise<void> => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify(signedNotice),
    };

    await authenticatedFetch(`${baseUrl}/notices/user/esign`, requestOptions);
};

export const setUserNoticeRead = async (id: number): Promise<void> => {
    const requestOptions: RequestInit = {
        method: 'POST',
    };

    await authenticatedFetch(`${baseUrl}/notices/user/${id}/read`, requestOptions);
};

export const deleteUserNotice = async (id: number): Promise<void> => {
    const requestOptions: RequestInit = {
        method: 'DELETE',
    };

    await authenticatedFetch(`${baseUrl}/notices/user/${id}/delete`, requestOptions);
};

export const uploadSignedFloodReport = async (noticeId, file): Promise<boolean> => {
    const formData = new FormData();
    formData.append('file', file);

    const requestOptions: RequestInit = {
        method: 'POST',
        body: formData,
    };

    const response = await authenticatedFetch(`${baseUrl}/notices/${noticeId}/signedFile`, requestOptions, false);
    return response.ok;
};

export interface IOutStandingDrawGridRow {
    loanId: number;
    drawBudgetId: number;
    address: string;
    committed: number;
    funded: number;
    available: number;
    bankAccountLast4Digits: number;
    closingDate: Date;
    latestDrawType: DrawType;
    percentFunded: number;

    isApproved: boolean;
    isFunded: boolean;
    isBudgetMaxed: boolean;
    canAddDraw: boolean;
    isOutOfBalance: boolean;
    borrowerSubmitted: boolean;
    needsBorrowerSignature: boolean;

    currentApproval: {
        approvedOn: Date;
        approvedBy: string;
    };

    drawHistoryLines: {
        drawNumber: number;
        borrowerRequested: Date;
        inspectionOrdered: Date;
        inspectedOn: Date;
        fundedOn: Date;
        amountFunded: number;
        amountInspected: number;
        amountRequested: number;
        isBalanceFlagged: boolean;
        drawType: string;
        notes: string;
    };
}

export const getOutstandingDrawGrid =
    (): ThunkAction<Promise<IOutStandingDrawGridRow[]>, ApplyRootStateType, unknown, AnyAction> => async () => {
        const requestOptions: RequestInit = { method: 'GET' };

        const response = await authenticatedFetch(`${baseUrl}/draw/outstandingDrawGrid`, requestOptions);

        if (response.ok) {
            const resultsJSON = await response.json();
            const results = resultsJSON as {
                result: IOutStandingDrawGridRow[];
            };
            if (results.result?.length) {
                return results.result;
            }
        }
    };

export interface IAddress {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    county?: string;
    country?: string;
}

export type EntityListResult = {
    result: [
        {
            entityId: number;
            entityName: string;
            entityStateIncorporated: string;
            entityType: number;
            masterNoteId?: number | undefined;
            address: IAddress;
        },
    ];
};

export enum ValidationMessageType {
    Error = 0,
    Warning = 1,
}

export type ValidationMessage = {
    messageType: ValidationMessageType;
    message: string;
};

export type ValidationMessagesResult = {
    hasErrors: boolean;
    hasWarnings: boolean;
    messages: [ValidationMessage];
};

export type UserExperienceSaveResult = {
    id: number;
    principalId: number;
    success: boolean;
};

export const getEntityList = async (): Promise<EntityListResult> => {
    const requestOptions: RequestInit = {
        method: 'GET',
    };

    const response = await authenticatedFetch(`${baseUrl}/apply/getentitylist`, requestOptions);
    if (response.ok) {
        const resultsJSON = await response.json();
        const results = resultsJSON as EntityListResult;

        return results;
    }
};

export const validateEntity = async (id: number, name: string, state: string): Promise<ValidationMessagesResult> => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify({ entityId: id ?? 0, name, state }),
    };

    const response = await authenticatedFetch(`${baseUrl}/apply/entities/validate`, requestOptions);

    if (response.ok) {
        const resultsJSON = await response.json();
        const results = resultsJSON as {
            result: ValidationMessagesResult;
        };

        return results.result;
    }
};

export type BorrowerInfo = {
    isFirstLoan: boolean;
    shouldAskLegalQuestions: boolean;
};

export const getIpAddress = (): ThunkAction<Promise<string>, ApplyRootStateType, unknown, AnyAction> => async () => {
    const requestOptions: RequestInit = {
        method: 'GET',
    };

    const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', requestOptions);

    if (response.ok) {
        const resultText = await response.text();
        const resultJson = (resultText ?? '')
            .trim()
            .split('\n')
            .reduce(function (obj, pair) {
                const tokens = pair.split('=');
                return ((obj[tokens[0]] = tokens[1]), obj);
            }, {});

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (resultJson as any).ip;
    } else {
        console.error('failed to retrieve user ip address');
        return '';
    }
};

export const getUserExperiences =
    (): ThunkAction<Promise<void>, ApplyRootStateType, unknown, AnyAction> => async (dispatch, getState) => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await authenticatedFetch(`${baseUrl}/experiences/apply/user/all`, requestOptions);
        if (response.ok) {
            const resultsJSON = await response.json();
            const results = resultsJSON as {
                result: UserStoreTypes.UserExperienceGridRecord[];
            };
            if (results.result?.length) {
                const experiences = results.result.map((entry) => {
                    return {
                        id: entry.id,
                        experienceTypeId: entry.experienceTypeId,
                        projectTypeId: entry.projectTypeId,
                        verificationStatusTypeId: entry.verificationStatusTypeId,
                        isVerified: entry.isVerified,
                        street1: entry.street1,
                        street2: entry.street2,
                        city: entry.city,
                        state: entry.state,
                        zipCode: entry.zipCode,
                        county: entry.county,
                        rowNumber: entry.rowNumber,
                    };
                });
                dispatch(updateUserExperiences(experiences));
            }
        }
    };

export const getUserExperienceItem = async (id: number): Promise<UserStoreTypes.UserExperience> => {
    const requestOptions: RequestInit = {
        method: 'GET',
    };

    const response = await authenticatedFetch(`${baseUrl}/experiences/apply/user/${id}`, requestOptions);
    if (response.ok) {
        const resultsJSON = await response.json();
        const results = resultsJSON as {
            result: UserStoreTypes.UserExperience;
        };
        return results.result;
    }
};

export const saveUserExperienceItem = async (
    userExperience: UserStoreTypes.UserExperienceLean
): Promise<UserExperienceSaveResult> => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify(userExperience),
    };

    const response = await authenticatedFetch(`${baseUrl}/experiences/apply/user`, requestOptions);

    if (response.ok) {
        const resultsJSON = await response.json();
        const results = resultsJSON as {
            result: {
                id: number;
                principalId: number;
                success: boolean;
            };
        };
        return results.result;
    }
};

export const validateUserExperience = async (userExperience: UserStoreTypes.UserExperienceLean): Promise<boolean> => {
    const requestOptions: RequestInit = {
        method: 'POST',
        body: JSON.stringify(userExperience),
    };

    const response = await authenticatedFetch(`${baseUrl}/experiences/apply/user/validate`, requestOptions);

    if (response.ok) {
        const resultsJSON = await response.json();
        const results = resultsJSON as {
            result: { isValid: boolean };
        };
        return results.result.isValid;
    }
};
